import {action, computed, observable, reaction, runInAction, toJS} from 'mobx';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import isUndefined from 'lodash/isUndefined';
import isNull from 'lodash/isNull';
import orderBy from 'lodash/orderBy';
import {
    actions,
    category,
    events,
    LessonNotificationType,
    NotificationType,
    RealtimeAction,
    TrackActionType,
    TrackPlayStatus,
    UploadType
} from '../../../constants/enums';
import {uploadToS3} from '../../../utils/awsUtility';
import {guid} from '../../../utils/commonUtility';
import {getId, getName, getNewSenderData, isGuest, isOwner, isStudent, isTeacher} from '../../../utils/userUtility';
import request from '../../../utils/request';
import {NOTIFICATION_ADD_TUNE, PROJECT} from '../../../constants/urlConstants';
import Player from '../../../stores/PlayerStore';
import {BpmCalculator} from '../../../utils/bpmCalculator';
import trackStore from '../../main/track/store';
import projectStore from '../../main/project/store';
import appStore from '../../app/store';
import audioStore from '../../../stores/AudioStore';
import uploadTrackStore from '../../common/uploadTrack/store';
import actionStore from '../../../stores/ActionStore';
import dialogStore from '../../dialog/store';
import recroderStore from '../../../components/recorder/store';
import TrackAction from '../../../stores/TrackActionStore';
import groupStore from '../../../containers/main/groups/store';
import {ATTACHMENT_BUCKET, BPM_DEFAULT, MAX_LOOPER_TRACKS, MIN_DISCOVERY_LESSON_ACCUMULATED_PLAYING_TIME} from '../../../constants/constants';
import {colorPaletteList, notation} from '../../../themes/colors';
import {getAudioContext, getAudioContextForIos, metronome} from '../../../utils/audioUtility';
import groupDefaultAvatar from '../../../assets/images/group-avatar-default.svg';
import {customEvent} from '../../../utils/analyticsUtil';
import notationViewerStore from '../../../stores/NotationViewerStore';
import {isIos} from '../../../utils/screenUtility';
import realtimeStore from '../../../stores/RealtimeStore';
import {diff} from 'json-diff';
import wizardStore from '../../studio/store';
import {translate} from '../../../utils/localizationUtility';

const toastOptions = {
    duration: 5000,
    animation: 'slide-up',
    className: 'centered',
    style: {
        background: notation,
        color: 'white',
        paddingLeft: 24,
        paddingRight: 24,
        fontSize: 14,
        fontWeight: 600
    }
};


class LooperStore extends Player {
    @observable isFetching = false;
    @observable isLooperPlaying = false;
    @observable isInSync = false;
    @observable isLooperLoaded = false;
    @observable isProjectLoadingToLooper = false;
    @observable isEditedProject = false;
    @observable editTimestamp = 0;
    @observable looperProject = this._initLooperProject();
    @observable playSlice = {start: null, end: null};
    @observable orderBy = {};
    @observable tracks = [];
    @observable duringSync = 0;
    @observable looperProjectName = this.looperProject.name || '';
    @observable defaultBPM = 120;
    @observable colorPalette = [];
    @observable isEmptyLooper = true;
    @observable metronomeVolume = 0.5;
    @observable isActiveMetronome = false;
    @observable masterVolume = 0.5;
    @observable activeExercise = {index: null, ctaDescription: ''};
    @observable countDownActive = false;
    @observable isCountDown = true;
    @observable processAudioPct = 0;
    @observable showLooperDrawer = false;
    @observable countDownValue = 4;
    @observable remoteAction = null;
    preventAutorun = false;

    @observable studioBeatsCount = null;


    accumulatedPlayingTime = 0;
    lastPlayTime = Date.now();

    updateTimestamp = 0;


    constructor() {
        super();

        // reaction(
        //     () => this.timeSignatureMeter,
        //     async () => {
        //         this.metronomeTrack = await this.registerMetronome(this.bpm, this.duration, this.timeSignatureMeter, this.metronomeTrack);
        //     },
        //     {fireImmediately: true}
        // );

        // sync steps
        reaction(() => wizardStore.currentSteps, (steps) => {
            this.updateSteps(steps)
        })

        this.initColorPalette();
    }

    @action updateSteps = (steps) => {
        try {
            const newSteps = steps.map(step => step.type);
            const currentSteps = this.looperProject.steps || [];
            this.looperProject.steps = newSteps;
            if (currentSteps.length > 0 && JSON.stringify(currentSteps) !== JSON.stringify(newSteps)) {
                this.setIsEditedProject(true);
            }
        } catch (err) {
            console.log(err);
        }
    }

    sendProjectUpdate = async () => {
        let tracks = toJS(this.tracks);
        tracks.forEach(t => {
            delete t.peaks;
            delete t.wave;
        });

        await realtimeStore.send(`project:${this.looperProject.id}`, {
            type: LessonNotificationType.LessonUpdate,
            objectId: this.looperProject.id,
            objectType: NotificationType.Lesson,
            project: Object.assign({}, toJS(this.looperProject), {loopers: tracks}, {actions: actionStore.getAll()}, {timestamp: Date.now()}, {updater: getId()})
        });
    };

    sendActionUpdate = async (action, additionalInfo = {}) => {
        if (!this.preventSend && (wizardStore.presented || wizardStore.presentMode)) {
            await realtimeStore.send(`project:${this.looperProject.id}`, {
                type: LessonNotificationType.LessonActionUpdate,
                objectId: this.looperProject.id,
                objectType: NotificationType.Lesson,
                action: action,
                additionalInfo: {...additionalInfo, name: getName()}
            });
        }
    };


    @action processActionUpdate = (action, additionalInfo = {}) => {
        const remoteName = additionalInfo.name;
        switch (action) {
            case RealtimeAction.Play:
                appStore.createToast({message: translate("realtime.audio.play", {remoteName}), ...toastOptions});
                break;
            case RealtimeAction.Pause:
                appStore.createToast({message: translate("realtime.audio.pause", {remoteName}), ...toastOptions});
                if (!this.isLooperPlaying && additionalInfo.seekTo >= 0) {
                    this.looperTracksSeekTo(additionalInfo.seekTo || 0, false, false, true);
                }
                break;
            case RealtimeAction.MetronomeOn:
                appStore.createToast({message: translate("realtime.audio.metronome.on", {remoteName}), ...toastOptions});
                break;
            case RealtimeAction.MetronomeOff:
                appStore.createToast({message: translate("realtime.audio.metronome.off", {remoteName}), ...toastOptions});
                break;
            case RealtimeAction.SeekTo:
                if (!this.isLooperPlaying) {
                    this.looperTracksSeekTo(additionalInfo.seekTo || 0, false, false, true);
                }
                break;
            case RealtimeAction.AudioRecord:
                appStore.createToast({message: translate("realtime.audio.recording.audio.record", {remoteName}), ...toastOptions});
                break;
            case RealtimeAction.AudioUpload:
                if (!additionalInfo.isDone) {
                    this.remoteAction = translate("realtime.audio.recording.audio.upload", {remoteName});
                } else {
                    this.remoteAction = null;
                }
                break;
            case RealtimeAction.VideoRecord:
                if (!additionalInfo.isDone) {
                    this.remoteAction = translate("realtime.audio.recording.video.record", {remoteName});
                } else {
                    this.remoteAction = null;
                }
                break;
            case RealtimeAction.VideoUpload:
                if (!additionalInfo.isDone) {
                    this.remoteAction = translate("realtime.audio.recording.video.upload", {remoteName});
                } else {
                    this.remoteAction = null;
                }
                break;
        }

    }


    @action
    async updateProject(project) {
        if (this.updateTimestamp <= project.timestamp && (wizardStore.presented || wizardStore.presentMode)) {
            this.preventAutorun = true;
            this.looperProject.lastUpdater = getId();
            this.updateTimestamp = project.timestamp;
            this.looperProject.timestamp = this.updateTimestamp;
            const projectDiff = diff(toJS(this.looperProject), project);
            this.isLooperLoaded = false;
            projectStore.setFileInput(this.looperProject);
            actionStore.init();
            if (!isEmpty(project.actions)) {
                actionStore.setMinUndoLength(project.actions.length);
                project.actions.forEach(projAction => actionStore.add(projAction));
            }

            console.log('project diff: ', projectDiff);
            await Promise.all(Object.keys(projectDiff).map(async deltaKey => {
                console.log('handing delta: ' + deltaKey);
                switch (deltaKey) {
                    case 'loopers':
                        await Promise.all(project.loopers.map(async looper => {
                            let trackIndex = this.tracks.findIndex(track => track.id === looper.id);
                            if (trackIndex >= 0) {
                                this.tracks[trackIndex].volume = looper.volume;
                                this.tracks[trackIndex].isOpen = looper.isOpen;
                                this.setVolume(this.tracks[trackIndex]);
                            } else {
                                await this.addToLooper(looper, false);
                            }
                        }));
                        await Promise.all(this.tracks.map(async track => {
                            if (project.loopers.findIndex(l => l.id === track.id) === -1) {
                                await this.removeFromLooper(track);
                            }
                        }));
                        break;
                    case 'slice':
                        this.setPlaySlice(project.slice.start, project.slice.end, project.slice.trackIndex, project.slice.splitIndex, project.slice.selectedBeats, true);
                        break;
                    case 'notations':
                        Object.assign(this.looperProject.notations, project.notations);
                        projectStore.setFileInput(this.loopedProject, false);
                        break;
                    case 'attachments':
                        Object.assign(this.looperProject.attachments, project.attachments);
                        projectStore.setFileInput(this.loopedProject, false);
                        break;
                    case 'masterVolume':
                        this.setMasterVolume(project.masterVolume);
                        break;
                    case 'description':
                        this.setProjectDescription(project.description);
                        break;
                    case 'videoUrls':
                        this.setProjectVideos(project.videoUrls);
                        projectStore.initVideos(this.loopedProject.videoUrls);
                        break;
                    case 'bpm':
                        await this.sync(project.bpm);
                        break;
                    case 'pitch':
                        this.setProjectPitch(project.pitch);
                        // await this.sync(project.bpm);
                        break;
                    case 'advancedMode':
                        this.setAdvancedMode(project.advancedMode)
                        break;
                    case 'masterVolumeOpen':
                        this.setMasterVolumeOpen(project.masterVolumeOpen);
                    default:
                        console.log('ignored delta: ' + deltaKey);
                        break;

                }
            }));
            this.preventAutorun = false;
            runInAction(() => {
                this.isLooperLoaded = true;
            });

            // await this.addProjectToLooper(project);
        } else {
            console.log('refusing to update older version');
        }
    }


    updateMetronome = async () => {
        await runInAction(async () => {
            if (this.bpm > 0) {
                this.metronomeTrack = await this.registerMetronome(this.bpm, this.duration, this.timeSignatureMeter, this.metronomeTrack);
                if (this.emptyMetronome) {
                    this.emptyMetronome.stop();
                }
                this.emptyMetronome = await metronome(this.bpm, this.timeSignatureMeter);
            }
            if (this.isActiveMetronome) {
                if (this.emptyMetronome) {
                    this.emptyMetronome.stop();
                } else {
                    this.emptyMetronome = await metronome(this.bpm, this.timeSignatureMeter);
                }

                if (!this.isLooperPlaying) {
                    this.emptyMetronome.play();
                }
            }
        })
    };


    /**
     * initialize the looper track to fit looper track model
     * @param originalTrack {Object}    The original track
     * @returns {Object}    Modified looper track
     */
    _initLooperTrack = (originalTrack) => {
        const track = Object.assign({}, originalTrack);
        this.initTrack(track);
        delete track.playerID;
        track.originalFileId = track.originalFileId || track.fileId;
        track.originalBPM = track.originalBPM || track.bpm;
        track.duration = track.originalDuration || track.duration;
        track.originalDuration = track.originalDuration || track.duration;
        track.hover = false;
        track.selectedBeats = [];
        track.startOffset = 0;
        track.mutedBeats = [];
        track.splits = [];
        track.color = this.generateColor(track.color || null);
        track.originalPitch = track.originalPitch || 0;
        track.isOpen = false;
        return track;
    };

    _initLooperProject = () => {
        return {
            duration: 0,
            loopers: [],
            bpm: null,
            actions: [],
            beatCount: null,
            name: this.looperProjectName,
            timeSignatureMeter: 4,
            numBeatsPerBar: 4,
            pitch: 0,
            masterVolume: 0.5,
            slice: {},
            gridDivision: null,
            skills: [],
            description: '',
            videoUrls: [],
            attachments: [],
            notations: [],
            backgroundImg: '',
            advancedMode: false,
            masterVolumeOpen: false,
            steps: null
        }
    };

    /**
     * bpm
     * @returns {*} the bpm of the leader track or null
     */

    @action
    setMasterVolume(volume, userAction = true) {
        this.masterVolume = volume;
        this.changeMasterVolume(this.looperTracks, volume);
        if (userAction) {
            this.setIsEditedProject(true);
        }
        this.looperProject.masterVolume = this.masterVolume;
    }

    @action
    setLooperVolume(object, volume) {
        this.changeVolume(object, volume);
        this.setIsEditedProject(true);
    }

    @computed
    get bpm() {
        return this.looperProject.bpm;
    }

    @computed
    get pitch() {
        return this.looperProject.pitch;
    }

    @computed
    get advancedMode() {
        return this.looperProject.advancedMode;
    }

    @computed
    get timeSignatureMeter() {
        return this.looperProject.timeSignatureMeter || 4;
    }

    @computed
    get numBeatsPerBar() {
        return this.looperProject.numBeatsPerBar || 4;
    }

    @computed
    get beatCount() {
        return this.looperProject.beatCount;
    }

    @computed
    get duration() {
        // return Math.max(this.looperProject.duration, this.minDuration);
        return this.looperProject.duration;
    }

    @computed
    get minDuration() {
        return Math.max(...this.tracks.map(track => track.duration || 0));
    }

    @computed
    get leaderTrack() {
        return this.tracks.reduce((leader, track) => {
            return (isEmpty(leader) || isUndefined(leader.duration) || leader.duration < track.duration) ? track : leader;
        }, {});
    }

    @computed
    get loadingProgress() {
        const tracksLoadingProgress = this.looperTracks.reduce((total, track) => {
            return total + track.downloadPct
        }, 0) / this.tracks.length;

        return tracksLoadingProgress * 0.8 + (1 - this.processAudioPct) * 0.2;
    }

    @computed
    get isOtherUserLesson() {
        return this.loopedProject.userId && this.loopedProject.userId !== getId();
    }

    @computed
    get showAssignment() {
        return isTeacher() && !isGuest() && (this.isOtherUserLesson || this.loopedProject.groupId);
    }

    @computed
    get assignmentAvatar() {
        return this.showAssignment && (this.isOtherUserLesson ? this.loopedProject.owner.avatar : groupDefaultAvatar);
    }

    @computed
    get assignmentName() {
        return this.showAssignment && (this.isOtherUserLesson ? this.loopedProject.owner.name : groupStore.getGroupName(this.loopedProject.groupId));
    }

    orderLooperTracks = (items) => {
        let ctas = items.filter(track => !track.id);
        ctas.sort((a, b) => {
            return (a.index - b.index);
        });
        let tracks = items.filter(track => track.id);
        tracks = !isEmpty(this.orderBy) ? orderBy(tracks, this.orderBy.field, this.orderBy.direction) : tracks;
        return ctas.concat(tracks);
    };

    /**
     * get all available feed tracks
     * @returns {Array}
     */
    @computed
    get looperTracks() {
        return this.tracks.filter(track => track.id);
    }

    @computed
    get allTracks() {
        return this.tracks.slice();
    }

    @computed
    get looperTracksWithMetronome() {
        const tracks = this.looperTracks.slice();
        if (this.metronomeTrack) {
            tracks.push(this.metronomeTrack);
        }
        return tracks;
    }

    @computed
    get isDownloading() {
        if (!isEmpty(this.looperTracks)) {
            return this.looperTracks.every(track => !track.loaded && track.bpm !== -1);
        }
        return false;
    }

    /**
     * Get looper project data
     * @returns {Object}
     */
    @computed
    get loopedProject() {
        return toJS(this.looperProject);
    }

    @computed
    get isLoopedProjectLesson() {
        return !isEmpty(this.loopedProject.assignedUsers) || this.loopedProject.assignedToAll;
    }

    /**
     * Check if play all is running
     */
    @computed
    get isPlayingAllTracks() {
        return this.isLooperPlaying && this.looperTracks.every(track => track.playStatus === TrackPlayStatus.Playing);
    }

    /**
     * Check if play all is running
     */
    @computed
    get isPlayingTracks() {
        return this.looperTracks.some(track => track.playStatus === TrackPlayStatus.Playing);
    }

    @computed
    get canPlayAll() {
        return !isEmpty(this.looperTracks) && this.duringSync === 0;
    }

    @computed
    get isLoopersEmpty() {
        return isEmpty(this.tracks);
    }

    @computed
    get descriptionText() {
        if (!this.loopedProject.description) return '';
        const p = document.createElement('p');
        p.innerHTML = this.loopedProject.description;
        return p.innerText;
    }

    @computed
    get isProjectEmpty() {
        const {videoUrls = [], attachments = [], notations = []} = this.loopedProject;
        return this.isLoopersEmpty && !this.descriptionText.length && !videoUrls[0] && !attachments.length && !notations.length;
    }

    @computed
    get playStatus() {
        return this.isPlayingTracks ? TrackPlayStatus.Playing : TrackPlayStatus.Stopped
    }

    @action
    setProjectDuration(val) {
        this.looperProject.duration = val;
    }

    @action
    setProjectGridDivision(val) {
        this.looperProject.gridDivision = val;
        this.setIsEditedProject(true);
    }

    @action
    setProjectBeatCount(val) {
        this.looperProject.beatCount = val;
    }

    @action setProjectName = (val) => {
        this.looperProjectName = val;
        this.looperProject.name = val;
        this.setIsEditedProject(true);
    };

    @action setProjectDescription = (val) => {
        this.looperProject.description = val;
        this.setIsEditedProject(true);
    };

    @action clearProjectLoopers = () => {
        this.tracks = [];
        this.looperProject.loopers = [];
        this.setIsEditedProject(true);
    };

    @action setProjectVideos = (videos) => {
        this.looperProject.videoUrls = videos;
        this.setIsEditedProject(true);
    };

    @action setProjectAttachments = (attachments, userAction = true) => {
        this.looperProject.attachments = attachments;
        if (userAction) this.setIsEditedProject(true);
    };

    @action uploadBlobAttachmentsToS3 = async () => {
        const files = [...this.looperProject.attachments, ...this.looperProject.notations];
        await Promise.all(files.map(async file => {
            if (file.blob && file.blob.size) {
                const key = `attachments/${guid()}`;
                await uploadToS3(file.blob, key, file.type, null, ATTACHMENT_BUCKET, true);
                runInAction(() => {
                    file.blob = null;
                    file.url = key;
                });
            }
        }));
    };

    @action setProjectNotations = (notations, userAction = true) => {
        this.looperProject.notations = notations;
        if (userAction) this.setIsEditedProject(true);
    };

    @action setProjectImage = (backgroundImg) => {
        this.looperProject.backgroundImg = backgroundImg;
        this.setIsEditedProject(true);
    };

    @action updateProjectForm = (form) => {
        //todo change this
        this.looperProject = {...this.looperProject, ...form};
        this.setIsEditedProject(true);
    };

    @action sendUpdateIfNeeded(force = false) {
        this.editTimestamp = Date.now();
        if ((force || this.isEditedProject) && this.editTimestamp && !this.preventAutorun && (wizardStore.presentMode || wizardStore.presented)) {
            console.log('sending update');
            setTimeout(() => {
                this.sendProjectUpdate()
            }, 0);
        }
    }

    @action setIsEditedProject = (val) => {
        this.isEditedProject = val;
        this.sendUpdateIfNeeded();
    };

    @action
    async setProjectBpm(val, userAction = true) {
        if (this.looperProject.bpm !== val) {
            this.looperProject.bpm = val;
            if (userAction) this.setIsEditedProject(true);
            await this.updateMetronome();
        }
    }

    @action
    setProjectPitch(val) {
        if (this.looperProject.pitch !== val) {
            this.looperProject.pitch = val;
            this.setIsEditedProject(true);
        }
    }

    @action
    setAdvancedMode(val) {
        if (this.looperProject.advancedMode !== val) {
            customEvent(events.CustomEvent, {category: isGuest() ? category.Discover : category.Studio, action: actions.ToggleAdvancedMode, label: 'Audio'});
            this.looperProject.advancedMode = val;
            this.sendUpdateIfNeeded(true);
        }
    }

    @action
    setMasterVolumeOpen(val) {
        if (this.looperProject.masterVolumeOpen !== val) {
            this.looperProject.masterVolumeOpen = val;
        }
    }


    @action
    setProjectTimeSignatureMeter(val) {
        this.looperProject.timeSignatureMeter = val;
    }

    @action
    setProjectNumBeatsPerBarMeter(val) {
        this.looperProject.numBeatsPerBar = val;
    }

    /**
     * set the looped project
     * @param project   {Object}    The project details
     */
    @action
    setLoopedProject(project) {
        this.looperProject = Object.assign({}, this._initLooperProject(), project);
        this.looperProjectName = project.name;
        this.setIsEditedProject(false);
        this.updateTimestamp = null;
    }

    /**
     * Set the looper play slice to play from time to time
     * @param start  {Number}   The slice start time
     * @param end  {Number}   The slice end time
     */
    @action setPlaySlice = (start, end, trackIndex, splitIndex, selectedBeats, userAction, ignoreNotation = false) => {
        if (this.playSlice?.start === start && this.playSlice?.end === end) {
            return;
        }
        this.playSlice = {start, end};
        // console.log('looper ' + JSON.stringify(this.playSlice));
        if (!ignoreNotation) {
            notationViewerStore.setSelection(start, end);
        }
        if (userAction) {
            let wasPlaying = this.isLooperPlaying;
            if (this.isLooperPlaying) {
                this.toggleLooperPlayPause(true, TrackPlayStatus.Paused, null, false, null, true);
            }

            if (start !== null) {
                this.looperProject.slice = {
                    start: start,
                    end: end,
                    bpm: this.bpm,
                    trackIndex: trackIndex,
                    splitIndex: splitIndex,
                    selectedBeats: selectedBeats
                };
            } else {
                this.looperProject.slice = {};
            }
            this.setIsEditedProject(true);
            if (wasPlaying) {
                setTimeout(() => {
                    this.toggleLooperPlayPause(false, TrackPlayStatus.Playing, this.isActiveMetronome, true);
                }, 0);
            }
        }
    };

    @action playPauseIfWasPlaying = () => {
        if (this.isLooperPlaying) {
            this.toggleLooperPlayPause(true, TrackPlayStatus.Paused, null, false, null, true);
            this.toggleLooperPlayPause(false, TrackPlayStatus.Playing, this.isActiveMetronome, true);
        }
    };

    @action setOrderBy = (by, desc = true) => {
        if (by) {
            this.orderBy = {field: by, direction: desc ? 'desc' : 'asc'};
            this.tracks.replace(orderBy(this.tracks.slice(), this.orderBy.field, this.orderBy.direction));
        } else {
            this.orderBy = {};
        }
    };

    async fixTrackAndSubscribe(track, bpmCalculator, toBpm) {
        const newToBPM = bpmCalculator !== null ? bpmCalculator.calculate(track.originalBPM) : toBpm;
        // let needToRegister = false;
        // const isMidi = track.fileType === 'audio/midi';
        // if ((newToBPM !== track.originalBPM || (pitch !== 0 && pitch !== track.pitch)) && !track.copyData && !isMidi) {
        //     // await updateAudioFileBPMPitch(track.originalFileId, track.originalBPM, newToBPM, pitch); // for now we ignore to response (which is the new bpm)
        //     needToRegister = true;
        // } else if (newToBPM === track.originalBPM && newToBPM !== track.bpm) {
        //     needToRegister = true;
        // }
        await runInAction(async () => {
            const ratio = track.originalBPM / newToBPM;
            const ratioFromLast = track.bpm / newToBPM;
            Object.assign(track, {
                id: track.id,
                bpm: newToBPM,
                duration: ratio * track.originalDuration
            });

            if (track.copyData) {
                track.copyData.startTime *= ratio;
                track.copyData.endTime *= ratio;
            }

            if (!track.playerID) {// ||  needToRegister ) {
                await this.subscribe(track, true, false, false);
            } else {
                this.changePlaybackRate([track], 1 / ratio, 1 / ratioFromLast, track.pitch);
            }
            this.setVolume(track);
        });
    }

    /**
     * Preform the sync functionality
     * @param newBPM  {Number}    The bpm to sync to
     * @param dontCalcStudioBeatCount {Boolean} Skip calc beats
     * @param trackToUpdate {Object} Updated track
     * @returns {Promise.<void>}
     */
    @action sync = async (newBPM, dontCalcStudioBeatCount = false, trackToUpdate) => {
        if (!isNumber(newBPM)) {
            newBPM = this.bpm;
        }
        if (isEmpty(this.looperTracks)) {
            await this.setProjectBpm(newBPM);
            return;
        }
        try {
            const currentProjectId = this.loopedProject.id;
            let toBPM = isNumber(newBPM) && newBPM !== -1 ? newBPM : this.looperTracks[0].bpm || 120;
            if (toBPM === -1) {
                toBPM = 120;
            }
            const bpmCalculator = toBPM === this.looperTracks[0].originalBPM ? new BpmCalculator(toBPM) : null;
            this.duringSync += 1;
            this.isFetching = true;
            this.isLooperLoaded = false;
            let wasPlaying = this.isLooperPlaying;
            if (this.isLooperPlaying) {
                this.toggleLooperPlayPause(true, TrackPlayStatus.Paused, null, false, null, true);
            }

            // await this.updateMetronome();

            const tracksToFix = trackToUpdate ? [trackToUpdate] : this.looperTracks;
            await Promise.all(tracksToFix.filter(track => !track.copyData && !track.isMetronome && track.bpm !== -1).map(async (track) => {
                track.pitch = this.looperProject.pitch - track.originalPitch;
                await this.fixTrackAndSubscribe(track, bpmCalculator, toBPM);
            }));

            // fix copied tracks
            await Promise.all(tracksToFix.filter(track => !!track.copyData && !track.isMetronome && track.bpm !== -1).map(async (track) => {
                track.pitch = this.looperProject.pitch - track.originalPitch;
                await this.fixTrackAndSubscribe(track, bpmCalculator, toBPM);
            }));

            notationViewerStore.setSpeed(toBPM / this.looperTracks[0].originalBPM, looperStore.bpm);
            if (currentProjectId === this.loopedProject.id) { // Make sure current project didn't change since sync started
                await this.setProjectBpm(toBPM);
                // this.setProjectDuration(this.minDuration);
                runInAction(() => {
                    this.tracks = this.orderLooperTracks(this.tracks);
                    this.isFetching = false;
                    this.isLooperLoaded = true;
                    this.isInSync = true;
                    this.duringSync -= 1;
                    if (isNumber(this.playSlice.start) && isNumber(this.playSlice.end)) {
                        let ratio = toBPM / (this.loopedProject.slice.bpm || this.bpm);
                        this.looperProject.slice.start /= ratio;
                        this.looperProject.slice.end /= ratio;
                        this.looperProject.slice.bpm = toBPM;
                        this.playSlice.start /= ratio;
                        this.playSlice.end /= ratio;
                        notationViewerStore.setSelection(this.playSlice.start, this.playSlice.end)
                    }

                    // to make sure the play runs in a different scope than the pause and the UI can catch up
                    setTimeout(async () => {
                        if (wasPlaying) {
                            await this.updateMetronome();
                            this.updateCurrentTime(this.looperTracks[0]);
                            notationViewerStore.seek(this.looperTracks[0].currentTime);
                            this.toggleLooperPlayPause(false, TrackPlayStatus.Playing, this.isActiveMetronome, true);
                        }
                    }, 0);
                });
            }
        } catch (err) {
            console.log(err);
            runInAction(() => {
                this.isFetching = false;
                this.isInSync = false;
                this.duringSync -= 1;
            });
        }
    };

    _notifyAddTrack = (track) => {
        if (!isOwner(track.owner.id)) {
            request(`${NOTIFICATION_ADD_TUNE}`, {
                method: 'POST',
                body: {
                    ...getNewSenderData(),
                    objectId: track.trackId,
                    objectType: NotificationType.TrackLooped
                }
            });
        }
    };

    @action initColorPalette = () => {
        this.colorPalette = colorPaletteList.map(({value}) => {
            return {
                color: value[0],
                isUsed: false
            };
        });
    };

    @action resetColorInPalette = (color) => {
        const index = this.colorPalette.findIndex(item => item.color === color);
        this.colorPalette[index].isUsed = false;
    };

    @action generateColor = (color) => {
        const palette = this.colorPalette;
        if (color) {
            for (let i = 0; i < palette.length; i += 1) {
                if (palette[i].color === color) {
                    palette[i].isUsed = true;
                    return color;
                }
            }
        }
        for (let i = 0; i < palette.length; i += 1) {
            if (!palette[i].isUsed) {
                palette[i].isUsed = true;
                return palette[i].color;
            }
        }
        return palette[0].color;
    };

    canAddTracks = () => {
        const fromExercise = !isNull(this.activeExercise.index);
        const onlyAudioTracks = this.tracks.filter(t => {
            return !t.ctaDescription
        });
        return !(onlyAudioTracks.length >= MAX_LOOPER_TRACKS && !fromExercise);

    }

    /**
     * Add track to the looper
     * @returns {Promise.<void>}
     */
    @action addToLooper = async (track, shouldNotify = true) => {
        if (!this.canAddTracks()) {
            appStore.createAlertFromMessageId('error', 'notifications.maxStems', {num: MAX_LOOPER_TRACKS}, 2000);
            return;
        }
        const trackIndex = this.tracks.findIndex(t => t.id === track.id);
        if (trackIndex === -1) {
            trackStore.updateField(track.id, 'addedToLoopers', true);
            this.isLooperLoaded = false;
            track.addedToLoopers = true;
            track.addCount += 1;
            track.originalPitch = this.pitch;
            track.pitch = track.originalPitch;
            track.isOpen = false;
            if (!isNull(this.activeExercise.index)) {
                track.color = this.activeExercise.color;
            }
            const isFirst = this.tracks.filter(track => track.bpm !== -1).length === 0;
            if (isFirst) {
                let object = {
                    id: 'global',
                    bpm: this.bpm,
                    tracks: this.looperTracks
                };
                const action = new TrackAction(TrackActionType.ChangeTSMeter, [], [track.tsBeatsPerBar, track.tsBeatsMeter, `${track.tsBeatsPerBar}/${track.tsBeatsMeter}`], object, this.getObjectBarLength(object), null, true, this.gridDivision);
                actionStore.add(action);
            }
            const trackCopy = this._initLooperTrack(track);

            const fromExercise = !isNull(this.activeExercise.index);
            if (!fromExercise) {
                this.tracks = this.orderLooperTracks([...this.tracks, trackCopy]);
            } else {
                this.tracks = this.tracks.map((track) => {
                    return track.index === this.activeExercise.index ? {...track, ...trackCopy} : track;
                });
            }
            this.setProjectBpm(isFirst ? this.leaderTrack.bpm : (this.bpm || this.leaderTrack.bpm));
            const selectedTrack = this.tracks.find(t => t.id === track.id);
            if (selectedTrack.bpm !== -1) {
                if (this.bpm) {
                    await this.sync(this.bpm, false, selectedTrack);
                } else {
                    await this.subscribe(selectedTrack, true);
                }
                /* jony-lint disable-complicated-condition-in-if-statement-instead-of-ternary-operator */
                let playStatus;
                if (this.isPlayingTracks || (this.tracks.length === 1 && selectedTrack.playStatus === TrackPlayStatus.Playing)) {
                    playStatus = TrackPlayStatus.Playing;
                } else {
                    playStatus = TrackPlayStatus.Paused;
                }
                /* jony-lint enable */

                this.updateCurrentTime(selectedTrack);
                let leaderCurrentTime = selectedTrack.currentTime;
                if (!isEmpty(this.leaderTrack)) {
                    this.updateCurrentTime(this.leaderTrack);
                    leaderCurrentTime = this.leaderTrack.currentTime;
                    notationViewerStore.setTrack(this.leaderTrack);
                }
                this.seekTo(selectedTrack, (leaderCurrentTime / selectedTrack.duration) - Math.floor(leaderCurrentTime / selectedTrack.duration));

                runInAction(() => {
                    this.isLooperLoaded = true;
                    if (isFirst) {
                        this.defaultBPM = this.leaderTrack.bpm;
                    }
                    this.setProjectBpm(this.bpm || this.leaderTrack.bpm);
                    // this.setProjectDuration(this.minDuration);
                    if (this.bpm) {
                        const bpmCalculator = new BpmCalculator(this.bpm);
                        const newBpm = bpmCalculator.calculate(selectedTrack.bpm);
                        this.isInSync = this.isInSync && newBpm === selectedTrack.bpm;
                    }
                    this.looperProject.loopers.replace(this.tracks.slice());
                });
                this.togglePlayPause(selectedTrack, playStatus);
            } else {
                runInAction(() => {
                    this.isLooperLoaded = true;
                });
            }
            if (shouldNotify) {
                this._notifyAddTrack(selectedTrack);
            }
            runInAction(() => {
                this.isEmptyLooper = false;
            });
            actionStore.setIsDirty(true);
        }
        runInAction(() => {
            this.setIsEditedProject(true);
        });
    };

    @action setIsEmptyLooper = (val) => {
        this.isEmptyLooper = val;
    };

    /**
     * Add project to the looper
     * @returns {Promise.<void>}
     */
    @action addProjectToLooper = async (project, isEditedProject = false) => {
        this.isProjectLoadingToLooper = true;
        this.isFetching = true;
        this.isLooperLoaded = false;
        this.clearLooper();
        this.looperProject = {...this._initLooperProject(), ...project};
        this.looperProjectName = project.name;
        projectStore.setFileInput(project);
        actionStore.init();
        this.processAudioPct = 1;
        let processAudioInterval = setInterval(() => {
            runInAction(() => {
                this.processAudioPct = this.processAudioPct / 1.2;
            });
        }, 1000);
        if (!isEmpty(project.actions)) {
            actionStore.setMinUndoLength(project.actions.length);
            project.actions.forEach(projAction => actionStore.add(projAction));
            actionStore.setIsDirty(false);
        }
        if (!isEmpty(project.loopers)) {
            await Promise.all(this.orderLooperTracks(project.loopers).map(async (track) => {
                if (track.id) {
                    const looperTrack = this._initLooperTrack(track);
                    trackStore.updateField(track.id, 'addedToLoopers', true);
                    await runInAction(async () => {
                        this.tracks.push(looperTrack);
                        const selectedTrack = this.tracks.find(t => t.id === track.id);
                        const ratio = selectedTrack.originalBPM / selectedTrack.bpm;
                        selectedTrack.duration = ratio * track.originalDuration;
                        selectedTrack.pitch = (project.pitch - selectedTrack.originalPitch) || 0;
                        if (!selectedTrack.playerID) {
                            await this.subscribe(selectedTrack, true);
                            this.setVolume(selectedTrack);
                        }
                    });
                } else {
                    runInAction(() => {
                        this.tracks.push(track);
                        this.generateColor(track.color);
                    });
                }
            }));
        }

        runInAction(() => {
            if (this.leaderTrack) {
                this.setProjectDuration(this.leaderTrack.duration);
            }
            let newBpm = this.bpm || project.bpm;
            // fix project bpm issues
            if (newBpm === -1 && this.leaderTrack) {
                newBpm = this.leaderTrack.bpm;
            }
            this.setProjectBpm(newBpm, false);
            this.setProjectPitch(project.pitch);
            this.updateMetronome();
            this.setMasterVolume(this.looperProject.masterVolume, false);
            this.defaultBPM = newBpm || BPM_DEFAULT;
            this.isEmptyLooper = false;
            this.setIsEditedProject(isEditedProject);
            this.updateTimestamp = null;
            this.isFetching = false;
            this.isLooperLoaded = true;
            clearInterval(processAudioInterval);
            this.processAudioPct = 0;
            this.isProjectLoadingToLooper = false;
        });
    };

    saveNewStemsAsTracks = async () => {
        const tracksToSave = this.tracks.filter(track => !track.saved && track.id);
        await trackStore.saveNewTracks(tracksToSave);
    };

    /**
     * Remove all tracks from the looper
     */
    @action clearLooper = () => {
        this.tracks.forEach((track) => {
            this.clearPlayer(track);
            trackStore.updateField(track.id, 'addedToLoopers', false);
            track.volume = 0.5;
        });
        this.tracks.replace([]);
        this.looperProjectName = '';
        this.looperProject = this._initLooperProject();
        this.isLooperPlaying = false;
        this.isEmptyLooper = true;
        this.setIsEditedProject(false);
        this.updateTimestamp = null;
        this.isFetching = false;
        this.isInSync = false;
        this.isActiveMetronome = false;
        this.duringSync = 0;
        this.accumulatedPlayingTime = 0;
        this.initColorPalette();
        actionStore.init();
        delete this.projectUsageId;
    };

    /**
     * Remove track from looper
     * @returns {Promise.<void>}
     */
    @action removeFromLooper = (track) => {
        const trackIndex = this.tracks.findIndex(t => t.id === track.id);
        if (trackIndex !== -1) {
            if (track.color) {
                this.resetColorInPalette(track.color);
            }
            actionStore.removeByTrackId(track.id);
            trackStore.updateField(track.id, 'addedToLoopers', false);
            if (track.ctaDescription) {
                const {index, ctaDescription, color} = track;
                this.tracks.splice(trackIndex, 1, {index, ctaDescription, color});
                this.tracks = this.orderLooperTracks(this.tracks);
            } else {
                this.tracks.splice(trackIndex, 1);
            }
            this.clearPlayer(track);
            this.setProjectDuration(this.minDuration);
        }
        if (this.tracks.length === 0) {
            actionStore.init();
            // this.looperProject = {...this.looperProject, ...this._initLooperProject()};
        }
        actionStore.setIsDirty(true);
        this.setIsEditedProject(true);
        if (this.isPlayingTracks) {
            setTimeout(() => {
                this.playPauseIfWasPlaying();
            }, 0);

        } else {
            this.stopAll(); // make sure metronome is stopped
        }
    };

    toggleLooperSolo = (track) => {
        this.setIsEditedProject(true);
        let hasUnmutedOtherTrack = this.tracks.some(t => {
            return track.id !== t.id && t.volume > 0
        });
        this.tracks.forEach(currTrack => {
            if (currTrack.id === track.id) {
                this.changeVolume(currTrack, track.volume || track.prevVolume || 0.5);
            } else {
                if (hasUnmutedOtherTrack) {
                    if (currTrack.volume > 0) {
                        this.changeVolume(currTrack, 0);
                    }
                } else {
                    this.changeVolume(currTrack, currTrack.volume || currTrack.prevVolume || 0.5);
                }
            }
        })
    };

    @action markTrack = (track) => {
        track.mark = !track.mark;
        this.setIsEditedProject(true);
    };

    @action pauseAllCallback = (loopEnd = false) => {
        if (loopEnd) {
            this.looperTracksSeekTo(this.playSlice.start || 0, false, true);
        } else {
            this.toggleLooperPlayPause(true, TrackPlayStatus.Paused, null, false, null, true);
            this.isLooperPlaying = false;
        }
    }

    /**
     * Toggle the play pause for the entire looper tracks
     */
    @action toggleLooperPlayPause = (isPlaying, pauseStatus = TrackPlayStatus.Stopped, playMetronome = null, immediate = false, recordCallback = null, suppressEvents = false) => {
        clearTimeout(this.emptyMetronomeTimeout);
        if (!isEmpty(this.looperTracks)) {
            const delay = immediate ? 0 : 60 / this.bpm * (4 / this.timeSignatureMeter) * this.numBeatsPerBar;
            if (delay === 0) {
                if (this.emptyMetronome) {
                    this.emptyMetronome.stop();
                }
                this.countDownValue = 0;
            } else {
                const timeoutDelay = delay - ((60 / this.bpm * (4 / this.timeSignatureMeter)) / 2);
                this.emptyMetronomeTimeout = setTimeout(() => {
                    if (this.emptyMetronome) {
                        this.emptyMetronome.stop();
                    }
                }, timeoutDelay * 1000);

                if (!isPlaying) {
                    this.countDownValue = this.numBeatsPerBar;
                    if (this.emptyMetronome) {
                        this.emptyMetronome.play();
                    }
                    this.countDownInterval = setInterval(() => {
                        runInAction(() => {
                            this.countDownValue--;
                            if (this.countDownValue === 0) {
                                clearInterval(this.countDownInterval);
                                this.countDownActive = false;
                            }
                        })
                    }, delay * 1000 / this.numBeatsPerBar);
                } else {
                    clearInterval(this.emptyMetronomeTimeout);
                    clearInterval(this.countDownInterval);
                    if (this.emptyMetronome) {
                        this.emptyMetronome.stop();
                    }
                    runInAction(() => {
                        this.countDownActive = false;
                    });
                }
            }
            this.isLooperPlaying = isPlaying;
            // if (this.isLooperPlaying) {
            //     this.pauseAll();
            // }
            // let tracks = this.looperTracks.slice();
            playMetronome = playMetronome || this.isActiveMetronome;

            if (this.metronomeTrack) {
                this.metronomeTrack.volume = playMetronome ? this.metronomeVolume : 0;
                this.setVolume(this.metronomeTrack);
            }

            if (this.isLooperPlaying) {
                notationViewerStore.pause();
            }

            const callback = () => {
                // console.log('callback play');
                let currentLatency = 0;
                try {
                    currentLatency = getAudioContext().currentTime - getAudioContext().getOutputTimestamp().contextTime;
                    // console.log(currentLatency);
                } catch (err) {
                }
                if (recordCallback) {
                    recordCallback();
                }
                setTimeout(() => {
                    notationViewerStore.play();
                }, currentLatency);
            };

            this.togglePlayPauseList(
                this.looperTracksWithMetronome,
                this.isLooperPlaying ? pauseStatus : TrackPlayStatus.Playing,
                this.playSlice.start || 0, this.playSlice.end || this.duration,
                false,
                immediate,
                delay,
                callback,
                suppressEvents
            );

            this.isLooperPlaying = !this.isLooperPlaying;
        }
    };

    @action startCountDown = async (skipSeek = false) => {
        if (!this.isPlayingTracks && !skipSeek && isIos()) {
            // make sure audio context is recreated if neccessary before it gets out of control
            getAudioContextForIos();
            recroderStore.refreshRecorder();
        }
        if (!this.emptyMetronome) {
            this.emptyMetronome = await metronome(this.bpm, this.timeSignatureMeter);
        } else {
            this.emptyMetronome.stop()
        }
        notationViewerStore.pause();
        if (!this.isPlayingTracks && !skipSeek) {
            if (this.playSlice.start && (this.leaderTrack.currentTime < this.playSlice.start || this.leaderTrack.currentTime > this.playSlice.end)) {
                this.looperTracksSeekTo(this.playSlice.start, false);
            }
        }
        if (!this.isPlayingTracks && this.isCountDown && !this.countDownActive) {
            this.countDownActive = true;
        }
        this.toggleLooperPlayPause(this.isLooperPlaying, TrackPlayStatus.Paused, this.isActiveMetronome, !this.isCountDown);

        if (!this.isLooperPlaying && this.isActiveMetronome) {
            await this.toggleMetronome(false);
        }
        this.handleAccumulatedPlayTimeStatusChanged();
    };

    handleAccumulatedPlayTimeStatusChanged = async (isOnExitLooper = false) => {
        const proj = this.loopedProject;
        if (!proj.rootProjectId) {
            return;
        }
        if ((!this.isPlayingTracks && !isOnExitLooper) || (this.isPlayingTracks && isOnExitLooper)) {
            const secondsPlayed = Math.round((Date.now() - this.lastPlayTime) / 1000);
            this.accumulatedPlayingTime += secondsPlayed;
            if (this.accumulatedPlayingTime >= MIN_DISCOVERY_LESSON_ACCUMULATED_PLAYING_TIME) {
                const resp = await request(`${PROJECT}/${proj.rootProjectId}/usage`, {
                    method: 'POST',
                    body: {durationInSecs: this.accumulatedPlayingTime, projectUsageId: this.projectUsageId}
                }, true);
                if (!isOnExitLooper) {
                    this.projectUsageId = resp.id;
                }
            }
        } else {
            this.lastPlayTime = Date.now();
        }
    }

    @action
    toggleMetronome = async (isActive) => {
        this.isActiveMetronome = isActive;
        if (!this.isPlayingTracks) {
            if (isActive) {
                if (this.emptyMetronome) {
                    this.emptyMetronome.stop();
                } else {
                    this.emptyMetronome = await metronome(this.bpm, this.timeSignatureMeter);
                }
                this.emptyMetronome.play();
            } else if (this.emptyMetronome) {
                this.emptyMetronome.stop();
            }
        } else {
            if (this.emptyMetronome) {
                this.emptyMetronome.stop();
            }
            if (this.metronomeTrack) {
                this.metronomeTrack.volume = isActive ? this.metronomeVolume : 0;
                this.setVolume(this.metronomeTrack);
            }
        }
    };

    @action metronomeVolumeChange = (val, force = false) => {
        this.metronomeVolume = val;
        if (this.metronomeTrack && (force || this.isActiveMetronome)) {
            this.metronomeTrack.volume = val;
            this.setVolume(this.metronomeTrack);
        }
        if (this.emptyMetronome) {
            this.emptyMetronome.changeVolume(val);
        }
    };

    /**
     * perform seek to for the looper, keeping the play status existing. seekTo 0 does reset
     * @param seekTo    {Number}  The current time to seek to
     */
    @action looperTracksSeekTo = (seekTo, skipNotationUpdate = false, countdown = false, preventSend = false) => {
        if (isNumber(seekTo)) {
            if (!preventSend) {
                this.sendActionUpdate(RealtimeAction.SeekTo, {seekTo: seekTo});
            }
            let wasPlaying = this.isLooperPlaying;
            countdown = countdown || this.countDownActive;
            if (this.isLooperPlaying) {
                // console.log('looper is playing')
                this.toggleLooperPlayPause(true, TrackPlayStatus.Paused);
            }
            setTimeout(() => {
                this.seekToList(this.looperTracks, seekTo / this.duration, this.duration);
                if (this.metronomeTrack) {
                    this.seekTo(this.metronomeTrack, seekTo / this.duration, this.duration);
                }
                if (!skipNotationUpdate) {
                    notationViewerStore.seek(seekTo);
                }
                // to make sure the play runs in a different scope than the pause and the UI can catch up
                setTimeout(() => {
                    if (wasPlaying) {
                        if (countdown) {
                            this.startCountDown(true);
                        } else {
                            this.toggleLooperPlayPause(false, TrackPlayStatus.Playing, this.isActiveMetronome, true, null, false);
                        }
                    }
                }, 0);
            }, 0);
        }
    };

    /**
     * update track current time
     * @param   trackID {String}    The selected track id
     */
    @action updateTrackCurrentTimeAndProgress = (trackID) => {
        const selectedTrack = this.looperTracks.find(track => track.id === trackID);
        this.updateCurrentTime(selectedTrack);
    };

    @action updateLooperTrackBpm = async (trackID, bpm) => {
        const looperTrack = this.looperTracks.find(track => track.id === trackID);
        if (looperTrack) {
            looperTrack.bpm = bpm;
            looperTrack.originalBPM = bpm;
            if (this.looperTracks.length === 1) {
                // i.e. this is the only track we need to also set the project bpm
                this.setProjectBpm(bpm);
            }
            await this.sync(this.bpm, false, looperTrack);
        }
    };

    @action onStudentsAssigned = (lessonId, students, isPublic) => {
        this.looperProject.assignedToAll = isPublic;
        this.looperProject.assignedUsers = students;
    };

    setEditExercise = (index) => {
        this.setActiveExercise(this.tracks.find(track => track.index === index));
    };

    @action
    editCtaInLooper = () => {
        const {index, ctaDescription} = this.activeExercise;
        this.tracks = this.tracks.map((track) => {
            return track.index === index ? {...track, ctaDescription: ctaDescription.trim()} : track;
        });
        this.setIsEditedProject(true);
    };

    @action
    removeExercise = (index) => {
        this.tracks = this.tracks.filter((track) => {
            if (track.index !== index) {
                return true;
            }
            this.resetColorInPalette(track.color);
            return false;
        });
        this.setIsEditedProject(true);
    };

    @action
    setActiveExercise = (exercise) => {
        this.activeExercise = exercise;
    };

    @action addCtaToLooper = (description) => {
        const cta = {
            ctaDescription: description.trim(),
            index: this.getNextCtaIndex(),
            color: this.generateColor()
        };
        this.tracks = this.orderLooperTracks([...this.tracks, cta]);
        this.setIsEditedProject(true);
    };

    getNextCtaIndex = () => {
        let result = -1;
        for (let i = this.tracks.length - 1; i >= 0; --i) {
            if (isNumber(this.tracks[i].index)) {
                result = this.tracks[i].index > result ? this.tracks[i].index : result;
            }
        }
        return result + 1;
    };

    @action toggleCountDown = () => {
        this.isCountDown = !this.isCountDown;
    };

    @action setCountDownActive = (val) => {
        this.countDownActive = val;
        // if (val && !this.countDownActive) {
        //     this.emptyMetronome.stop();
        // }
    };

    @action setIsProjectLoadingToLooper = (val) => {
        this.isProjectLoadingToLooper = val;
    };

    @action toggleLooperDrawer = () => {
        this.showLooperDrawer = !this.showLooperDrawer;
        this.sendDrawerEvent();
    };

    sendDrawerEvent = () => {
        if (this.showLooperDrawer) {
            customEvent(events.CustomEvent, {category: category.Studio, action: actions.OpenDrawer});
        } else {
            customEvent(events.CustomEvent, {category: category.Studio, action: actions.CloseDrawer});
        }
    };


    @action setShowLooperDrawer = (val) => {
        this.showLooperDrawer = val;
        this.sendDrawerEvent();
    };

    @action updateTrackInLooper = (updateTrack) => {
        let track = this.tracks.find(track => track.id === updateTrack.id);
        if (track) {
            Object.assign(track, updateTrack, {name: track.name});
            this.sync(this.bpm, false, track);
        }
    }

    @computed
    get isStudentAssignment() {
        return isStudent() && !isUndefined(this.loopedProject.parentOwnerId);
    }

    @action audioDragDrop = async (event) => {
        event.target.classList.remove('drag-over');
        if (event.dataTransfer.types.includes('Files')) {
            const file = event.dataTransfer.files[0];
            if (file.type.includes('audio')) {
                uploadTrackStore.setUploadType(UploadType.UploadFile);
                uploadTrackStore.onFileChange('file', file.name, [file]);
                audioStore.createAudioUrl(file);
                dialogStore.openUploadDialog();
            } else {
                appStore.createAlertFromMessageId('error', 'general.error.audioFile', 3000);
            }
        }
        if (event.dataTransfer.types.includes('track')) {
            const track = JSON.parse(event.dataTransfer.getData('track'));
            await this.addToLooper(track);
            trackStore.resetTrack(track);
        }
    };

    @action stopLooper = (clearNotations = false) => {
        if (this.isLooperPlaying) {
            this.toggleLooperPlayPause(true, TrackPlayStatus.Paused, null, false, null, true);
        }
        this.stopAll();
        if (clearNotations) {
            notationViewerStore.pause();
            notationViewerStore.clear();
        }
        if (this.emptyMetronome) {
            this.emptyMetronome.stop();
        }
    };

    @action setProjectSkills = (skills) => {
        this.looperProject = {...this.looperProject, skills};
        this.setIsEditedProject(true);
    };

    @action incrementProjectPracticeTime = (durationInSecs) => {
        console.log('increment');
        this.looperProject.practiceTime = this.looperProject.practiceTime || [];
        if (this.looperProject.practiceTime.length === 0) {
            const now = Date.now();
            this.looperProject.practiceTime.push({startDate: now, endDate: now, durationInSeconds: 0});
        }
        this.looperProject.practiceTime[this.looperProject.practiceTime.length - 1].durationInSeconds += durationInSecs;
    }

}

const looperStore = new LooperStore();
export default looperStore;
